import { useEffect, useState } from "react";
import { Link, NavLink, useLocation } from "react-router-dom";
import { AnimatePresence, motion } from "framer-motion";
import { Menu, X, ArrowRight } from "lucide-react";
import { NAV_LINKS, waUrl, WA_GENERAL } from "../config/site";
import WhatsAppIcon from "./WhatsAppIcon";

const Wordmark = () => (
  <Link to="/" data-testid="navbar-brand-logo" className="flex items-center gap-2.5" aria-label="Cleef Empire — home">
    <img src="/assets/logo.png" alt="Cleef Empire logo" className="h-9 w-auto" width="36" height="36" />
    <span className="font-display text-xl font-extrabold leading-none tracking-tight">
      <span className="text-cleef">Cleef</span> <span className="text-obsidian">Empire</span>
    </span>
  </Link>
);

export default function Header() {
  const [open, setOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const location = useLocation();

  useEffect(() => setOpen(false), [location.pathname]);
  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 24);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);
  useEffect(() => {
    document.body.style.overflow = open ? "hidden" : "";
    return () => (document.body.style.overflow = "");
  }, [open]);

  return (
    <>
      <header
        data-testid="site-header"
        className={`fixed inset-x-0 top-0 z-50 border-b transition-all duration-300 ${
          scrolled ? "border-obsidian/10 bg-ivory/90 backdrop-blur-xl" : "border-transparent bg-ivory/70 backdrop-blur-md"
        }`}
      >
        <div className="container-site flex h-16 items-center justify-between gap-4 lg:h-20">
          <Wordmark />
          <nav className="hidden items-center gap-7 xl:gap-8 lg:flex" aria-label="Primary">
            {NAV_LINKS.map((l) => (
              <NavLink
                key={l.to}
                to={l.to}
                data-testid={`nav-link-${l.label.toLowerCase().replace(/\s+/g, "-")}`}
                className={({ isActive }) =>
                  `relative text-[13px] font-medium uppercase tracking-[0.1em] transition-colors duration-200 after:absolute after:-bottom-1.5 after:left-0 after:h-[2px] after:bg-cleef after:transition-all after:duration-300 ${
                    isActive ? "text-obsidian after:w-full" : "text-obsidian/55 hover:text-obsidian after:w-0"
                  }`
                }
              >
                {l.label}
              </NavLink>
            ))}
          </nav>
          <div className="flex items-center gap-2.5">
            <a
              href={waUrl(WA_GENERAL)}
              target="_blank"
              rel="noopener noreferrer"
              data-event="whatsapp_click"
              data-testid="header-whatsapp-btn"
              aria-label="Chat with Cleef Empire on WhatsApp"
              className="hidden h-10 w-10 items-center justify-center border border-obsidian/15 text-obsidian transition-colors duration-200 hover:border-[#25D366] hover:text-[#128C7E] sm:flex"
            >
              <WhatsAppIcon className="h-[18px] w-[18px]" />
            </a>
            <Link to="/bulk-quote" data-event="bulk_quote_click" data-testid="header-cta-bulk-quote" className="btn-primary hidden !px-5 !py-2.5 !text-xs sm:inline-flex">
              Get Bulk Quote
            </Link>
            <button
              onClick={() => setOpen(true)}
              data-testid="mobile-menu-open-btn"
              aria-label="Open menu"
              className="flex h-10 w-10 items-center justify-center border border-obsidian/15 text-obsidian lg:hidden"
            >
              <Menu className="h-5 w-5" />
            </button>
          </div>
        </div>
      </header>

      <AnimatePresence>
        {open && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.25 }}
            className="fixed inset-0 z-[60] flex flex-col bg-ivory"
            data-testid="mobile-menu"
          >
            <div className="container-site flex h-16 items-center justify-between">
              <Wordmark />
              <button
                onClick={() => setOpen(false)}
                data-testid="mobile-menu-close-btn"
                aria-label="Close menu"
                className="flex h-10 w-10 items-center justify-center border border-obsidian/15"
              >
                <X className="h-5 w-5" />
              </button>
            </div>
            <nav className="container-site mt-6 flex flex-1 flex-col" aria-label="Mobile">
              {NAV_LINKS.map((l, i) => (
                <motion.div
                  key={l.to}
                  initial={{ opacity: 0, x: -18 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ delay: 0.06 + i * 0.05, duration: 0.4 }}
                >
                  <NavLink
                    to={l.to}
                    data-testid={`mobile-nav-link-${l.label.toLowerCase().replace(/\s+/g, "-")}`}
                    className={({ isActive }) =>
                      `group flex items-center justify-between border-b border-obsidian/10 py-4 font-display text-2xl font-bold tracking-tight ${
                        isActive ? "text-cleef" : "text-obsidian"
                      }`
                    }
                  >
                    {l.label}
                    <ArrowRight className="h-5 w-5 opacity-0 transition-opacity group-hover:opacity-100" />
                  </NavLink>
                </motion.div>
              ))}
            </nav>
            <div className="container-site grid grid-cols-2 gap-3 pb-10 pt-4">
              <Link to="/bulk-quote" data-event="bulk_quote_click" data-testid="mobile-menu-quote-btn" className="btn-primary">
                Get Bulk Quote
              </Link>
              <a
                href={waUrl(WA_GENERAL)}
                target="_blank"
                rel="noopener noreferrer"
                data-event="whatsapp_click"
                data-testid="mobile-menu-whatsapp-btn"
                className="btn-dark !bg-[#128C7E]"
              >
                <WhatsAppIcon className="h-4 w-4" /> WhatsApp
              </a>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
}
